import { useEffect, useMemo, useState } from 'react'
import {
  useOrphanCleanupPreviewQuery,
  useOrphanCleanupRunQuery,
  useOrphanCleanupStatusQuery,
  useStartOrphanCleanupMutation,
  type OrphanCleanupProgress,
  type OrphanCleanupRun
} from '@axilon-apps/domains-ontology-ui-state'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

const PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 250

export function CleanupOrphanTagsDialog({
  open,
  onOpenChange,
  onCleanedUp,
  embedded = false
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCleanedUp?: () => void
  embedded?: boolean
}) {
  const [startCleanup, startState] = useStartOrphanCleanupMutation()
  const [typedSearch, setTypedSearch] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  // The run being followed, and whether it was someone else's already under
  // way when this dialog opened rather than one started here.
  const [jobId, setJobId] = useState<string | null>(null)
  const [attached, setAttached] = useState(false)
  // The server memoizes one classification per organization, so paging and
  // filtering reuse it and the list can't shift mid-review. A fresh one is
  // only worth asking for when the dialog opens, and after a run failed
  // because the set had moved.
  const [refresh, setRefresh] = useState(true)

  useEffect(() => {
    if (open) return
    setTypedSearch('')
    setSearch('')
    setPage(1)
    setError(null)
    setRefresh(true)
    // Dropped rather than remembered: reopening asks the server what is
    // running, so a run that finished while the dialog was shut is not
    // presented as still going.
    setJobId(null)
    setAttached(false)
  }, [open])

  useEffect(() => {
    if (typedSearch === search) return
    const id = setTimeout(() => setSearch(typedSearch), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [typedSearch, search])

  // A run started from another tab, or before a reload, is adopted so the
  // dialog never offers to start a second fleet-wide delete alongside it.
  const { data: status } = useOrphanCleanupStatusQuery({ skip: !open || jobId !== null })
  useEffect(() => {
    if (!open || jobId || !status?.running || !status.jobId) return
    setJobId(status.jobId)
    setAttached(true)
  }, [open, jobId, status])

  const { run, error: runError } = useOrphanCleanupRunQuery(open ? jobId : null)
  const runState = run?.state ?? ''
  const finished = runState === 'done'
  const failed = runState === 'error'
  // True from the moment the delete is handed off, including the gap before the
  // run's id comes back: the review list has stopped being what this dialog is
  // about.
  const following = jobId !== null || startState.isLoading
  const running = following && !finished && !failed

  const {
    data: preview,
    isLoading,
    error: previewError,
    refetch
  } = useOrphanCleanupPreviewQuery(
    { search, page, pageSize: PAGE_SIZE, refresh },
    { skip: !open || jobId !== null }
  )

  const totalPages = useMemo(() => {
    if (!preview || preview.matchedTags === 0) return 1
    return Math.ceil(preview.matchedTags / PAGE_SIZE)
  }, [preview])

  // The deleted tags are gone from the registry now, so whatever is behind
  // this dialog is stale.
  useEffect(() => {
    if (!finished) return
    onCleanedUp?.()
    // onCleanedUp is a refetch trigger from the caller and is not stable
    // across renders; keying on the run keeps this to once per run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, jobId])

  const backToList = () => {
    setJobId(null)
    setAttached(false)
    setError(null)
    setPage(1)
    setTypedSearch('')
    setSearch('')
    setRefresh(true)
    // Explicit: the reader may already be on page one with no filter, in which
    // case nothing above changes the query and nothing refetches.
    refetch()
  }

  const nothingToDo = Boolean(preview) && preview?.totalTags === 0
  // Paging and filtering keep the same reviewed set, so the button stays live
  // through those. A fresh classification in flight is about to replace the
  // fingerprint, so it does not.
  const confirmDisabled = running || (isLoading && refresh) || !preview || preview.totalTags === 0

  const handleConfirm = async () => {
    if (!preview) return
    setError(null)
    try {
      const started = await startCleanup({ fingerprint: preview.fingerprint })
      setJobId(started.jobId)
      setAttached(started.alreadyRunning)
    } catch (err) {
      setError(String(err))
    }
  }

  const description = (
    <div className="min-w-0 space-y-2 text-sm text-muted-foreground">
      <p className="text-pretty break-words">
        {following
          ? attached
            ? 'A cleanup was already running for this organization, so this page is following that run.'
            : 'The cleanup runs on the server. You can leave this page and return while it continues.'
          : 'Removes scrape registrations whose graph tags no longer exist on any live branch, plus the endpoint credentials they strand. Review the full list below — everything in it is deleted together, along with its history.'}
      </p>
      {error ? (
        <p className="break-words text-st-down" data-testid="cleanup-orphan-error">
          {error}
        </p>
      ) : null}
      {previewError ? (
        <p className="break-words text-st-down" data-testid="cleanup-orphan-preview-error">
          Failed to preview cleanup: {previewError}
        </p>
      ) : null}
    </div>
  )

  const content = (
    <div
      className={`flex min-h-0 min-w-0 flex-col gap-3 ${embedded ? '' : 'flex-1 overflow-hidden'}`}
    >
      {following ? <CleanupRunProgress run={run} readError={runError} /> : null}

      {!following && isLoading && !preview ? (
        <p className="text-sm text-muted-foreground" data-testid="cleanup-orphan-preview-loading">
          Checking for unused tags…
        </p>
      ) : null}

      {!following && nothingToDo ? (
        <p className="text-sm text-muted-foreground" data-testid="cleanup-orphan-empty">
          Nothing to clean up.
        </p>
      ) : null}

      {!following && preview && preview.totalTags > 0 ? (
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col gap-3"
          data-testid="cleanup-orphan-preview"
        >
          <CleanupTotals
            tags={preview.totalTags}
            endpoints={preview.totalEndpoints}
            readings={preview.totalReadings}
            readingsCapped={preview.totalReadingsCapped}
          />

          <GatewayBreakdown gateways={preview.gateways} />

          <Input
            value={typedSearch}
            placeholder="Filter by tag path or gateway…"
            aria-label="Filter the deletion list"
            data-testid="cleanup-orphan-search"
            onChange={(e) => {
              setTypedSearch(e.target.value)
              setPage(1)
              setRefresh(false)
            }}
          />

          <TagList
            rows={preview.rows}
            matched={preview.matchedTags}
            total={preview.totalTags}
            searching={search !== ''}
          />

          {totalPages > 1 ? (
            <Pager
              page={preview.page}
              totalPages={totalPages}
              disabled={isLoading}
              onPage={(next) => {
                setPage(next)
                setRefresh(false)
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )

  const actions = (
    <>
      {failed ? (
        <>
          {!embedded ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : null}
          <Button onClick={backToList} data-testid="cleanup-orphan-retry">
            Back to the list
          </Button>
        </>
      ) : running || finished ? (
        !embedded ? (
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="cleanup-orphan-close"
          >
            {finished ? 'Done' : 'Close'}
          </Button>
        ) : null
      ) : (
        <>
          {!embedded ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          ) : null}
          <Button
            variant="destructive"
            className="max-w-full"
            onClick={() => void handleConfirm()}
            disabled={confirmDisabled}
            data-testid="cleanup-orphan-confirm"
          >
            {preview && preview.totalTags > 0
              ? `Delete ${preview.totalTags.toLocaleString()} tag${preview.totalTags === 1 ? '' : 's'}`
              : 'Delete unused tags'}
          </Button>
        </>
      )}
    </>
  )

  if (embedded) {
    return (
      <div className="flex min-w-0 flex-col gap-4">
        <div className="shrink-0">{description}</div>
        {content}
        <div className="flex shrink-0 justify-start gap-2">{actions}</div>
      </div>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
      <AlertDialogContent className="flex max-h-[min(44rem,calc(100vh-2rem))] w-[calc(100%-2rem)] max-w-3xl min-w-0 flex-col overflow-hidden">
        <AlertDialogHeader className="min-w-0 shrink-0">
          <AlertDialogTitle>Clean up unused tags</AlertDialogTitle>
          <AlertDialogDescription asChild>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {content}
        <AlertDialogFooter className="min-w-0 shrink-0 sm:flex-wrap">{actions}</AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function CleanupTotals({
  tags,
  endpoints,
  readings,
  readingsCapped
}: {
  tags: number
  endpoints: number
  readings: number
  readingsCapped: boolean
}) {
  return (
    <p className="text-sm text-st-down" data-testid="cleanup-orphan-totals">
      Will delete <strong>{tags.toLocaleString()}</strong> tag{tags === 1 ? '' : 's'}
      {endpoints > 0 ? (
        <>
          , <strong>{endpoints.toLocaleString()}</strong> endpoint
          {endpoints === 1 ? '' : 's'}
        </>
      ) : null}
      , and{' '}
      <strong>
        {readings.toLocaleString()}
        {readingsCapped ? '+' : ''}
      </strong>{' '}
      historical reading{readings === 1 && !readingsCapped ? '' : 's'}. This cannot be undone.
    </p>
  )
}

/** Plain-language description of what the run is doing right now. */
function phaseLabel(progress: OrphanCleanupProgress): string {
  switch (progress.phase) {
    case 'classifying':
      return 'Working out which tags are still unused…'
    case 'readings':
      return progress.totalTags > 0
        ? `Deleting history — ${progress.pairsDone.toLocaleString()} of ${progress.totalTags.toLocaleString()} tags cleared`
        : 'Deleting history…'
    case 'tags':
      return 'Removing the tag registrations…'
    case 'endpoints':
      return 'Removing the endpoints they stranded…'
    case 'done':
      return 'Cleanup finished.'
    default:
      return 'Starting…'
  }
}

const EMPTY_PROGRESS: OrphanCleanupProgress = {
  phase: '',
  totalTags: 0,
  pairsDone: 0,
  readingsDeleted: 0,
  tagsDeleted: 0,
  endpointsDeleted: 0
}

function CleanupRunProgress({
  run,
  readError
}: {
  run: OrphanCleanupRun | undefined
  readError: string | undefined
}) {
  const progress = run?.progress ?? EMPTY_PROGRESS
  const failed = run?.state === 'error'
  const done = run?.state === 'done'
  // Tags cleared is the only denominator a run knows up front. The reading
  // total is not one: the preview's own count is capped.
  const percent =
    progress.totalTags > 0
      ? Math.min(100, Math.round((progress.pairsDone / progress.totalTags) * 100))
      : 0

  if (failed) {
    const setChanged = (run?.error ?? '').includes('orphan set changed')
    return (
      <div className="min-w-0 space-y-2" data-testid="cleanup-orphan-run-failed">
        <p className="break-words text-sm text-st-down">
          {setChanged
            ? 'The list changed before the cleanup started, so nothing was deleted. Read it again and check before deleting.'
            : `The cleanup stopped: ${run?.error || 'unknown error'}`}
        </p>
        {!setChanged && progress.readingsDeleted > 0 ? (
          <p className="text-xs text-muted-foreground">
            {progress.readingsDeleted.toLocaleString()} readings had already been deleted before it
            stopped. Those tags are still registered — run the cleanup again to finish.
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="min-w-0 space-y-3" data-testid="cleanup-orphan-run">
      <p className="text-sm" data-testid="cleanup-orphan-run-phase">
        {phaseLabel(progress)}
      </p>
      {!done ? (
        <div
          className="h-1.5 w-full overflow-hidden rounded bg-border"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Cleanup progress"
        >
          <div
            className="h-full bg-ink-3 transition-[width] duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      ) : null}
      <ul
        className="space-y-1 text-xs text-muted-foreground"
        data-testid="cleanup-orphan-run-counts"
      >
        <li>{progress.readingsDeleted.toLocaleString()} readings deleted</li>
        <li>{progress.tagsDeleted.toLocaleString()} tags deleted</li>
        <li>{progress.endpointsDeleted.toLocaleString()} endpoints deleted</li>
      </ul>
      {readError ? (
        <p className="break-words text-xs text-st-down" data-testid="cleanup-orphan-run-read-error">
          Lost track of the cleanup ({readError}). It is still running on the server — this will
          pick it back up.
        </p>
      ) : null}
    </div>
  )
}

function GatewayBreakdown({
  gateways
}: {
  gateways: Array<{ sourceUrl: string; tags: number; endpointRemoved: boolean }>
}) {
  if (gateways.length === 0) return null
  return (
    <ul
      className="max-h-28 min-w-0 shrink-0 space-y-1 overflow-y-auto text-xs"
      data-testid="cleanup-orphan-gateways"
    >
      {gateways.map((gw) => (
        <li key={gw.sourceUrl} className="flex min-w-0 items-baseline justify-between gap-3">
          <span className="truncate font-mono text-ink-3">{gw.sourceUrl}</span>
          <span className="shrink-0 text-muted-foreground">
            {gw.tags.toLocaleString()} tag{gw.tags === 1 ? '' : 's'}
            {gw.endpointRemoved ? ' · endpoint removed' : ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

function TagList({
  rows,
  matched,
  total,
  searching
}: {
  rows: Array<{ tagPath: string; sourceUrl: string }>
  matched: number
  total: number
  searching: boolean
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1">
      <p
        className="shrink-0 text-xs text-muted-foreground"
        data-testid="cleanup-orphan-match-count"
      >
        {searching
          ? `${matched.toLocaleString()} of ${total.toLocaleString()} shown — filtering the list does not change what is deleted.`
          : `Showing all ${total.toLocaleString()}.`}
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="cleanup-orphan-no-matches">
          No tags match that filter.
        </p>
      ) : (
        <ul
          className="min-h-0 min-w-0 flex-1 divide-y divide-border overflow-y-auto rounded border border-border font-mono text-12"
          data-testid="cleanup-orphan-rows"
        >
          {rows.map((row) => (
            <li
              key={`${row.sourceUrl}:${row.tagPath}`}
              className="flex min-w-0 items-baseline justify-between gap-3 px-2 py-1"
            >
              <span className="break-all">{row.tagPath}</span>
              <span className="shrink-0 truncate text-ink-3">{row.sourceUrl}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Pager({
  page,
  totalPages,
  disabled,
  onPage
}: {
  page: number
  totalPages: number
  disabled: boolean
  onPage: (page: number) => void
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 text-xs">
      <button
        type="button"
        className="rounded border border-border px-2 py-1 disabled:opacity-50"
        disabled={disabled || page <= 1}
        data-testid="cleanup-orphan-prev"
        onClick={() => onPage(page - 1)}
      >
        Previous
      </button>
      <span className="text-muted-foreground" data-testid="cleanup-orphan-page">
        Page {page.toLocaleString()} of {totalPages.toLocaleString()}
      </span>
      <button
        type="button"
        className="rounded border border-border px-2 py-1 disabled:opacity-50"
        disabled={disabled || page >= totalPages}
        data-testid="cleanup-orphan-next"
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
    </div>
  )
}
